import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Store, Plus, Pencil, Trash2, ImageIcon, Sparkles, ExternalLink, Loader2, Tag, Eye, MessageCircle, GripVertical, Users, X, Search, Copy, EyeOff, RotateCcw, Link2 } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { useLojas } from "@/hooks/useLojas";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;
const slugify = (s) => (s || "").normalize("NFKD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-zA-Z0-9]+/g, "-").replace(/^-+|-+$/g, "").toLowerCase();
const fmtBRL = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(v) || 0);

const EMPTY = {
  nome: "", preco: "", preco_promocional: "", categoria: "", descricao: "",
  codigo: "", custo: "", vender_por: "Unidade", destacar: false, exibir_catalogo: true, foto_path: null, foto_url: null, fotos_extras: [], fotos_extras_urls: [],
};

export default function CatalogoAdmin() {
  const lojas = useLojas();
  const [loja, setLoja] = useState(null);
  const [produtos, setProdutos] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [uploadingExtra, setUploadingExtra] = useState(false);
  const [extraProgresso, setExtraProgresso] = useState("");
  const [fundoFalhou, setFundoFalhou] = useState(false);

  const [catOrdem, setCatOrdem] = useState([]);
  const [leadsOpen, setLeadsOpen] = useState(false);
  const [leads, setLeads] = useState([]);
  const [busca, setBusca] = useState("");
  const [catFiltro, setCatFiltro] = useState("");
  const [ordemSort, setOrdemSort] = useState("manual");
  const [aba, setAba] = useState("ativos");
  const [linksOpen, setLinksOpen] = useState(false);
  const fileRef = useRef(null);
  const extraRef = useRef(null);
  const dragProd = useRef(null);
  const dragCat = useRef(null);
  const reqRef = useRef(0);

  const carregar = useCallback(async (l) => {
    const rid = ++reqRef.current;
    try {
      const [rp, rc] = await Promise.all([
        axios.get(`${API}/catalogo/vitrine`, { params: { loja: l } }),
        axios.get(`${API}/catalogo/categorias/ordem`, { params: { loja: l } }),
      ]);
      if (rid !== reqRef.current) return;
      const prods = rp.data.produtos || [];
      setProdutos(prods);
      const presentes = [...new Set(prods.map((p) => p.categoria).filter(Boolean))];
      const salvo = (rc.data.categorias || []).filter((c) => presentes.includes(c));
      const novos = presentes.filter((c) => !salvo.includes(c));
      setCatOrdem([...salvo, ...novos]);
    } catch { if (rid === reqRef.current) toast.error("Erro ao carregar produtos"); }
  }, []);

  useEffect(() => { if (loja) { setProdutos([]); setCatOrdem([]); carregar(loja); } }, [loja, carregar]);

  const abrirNovo = () => { setEditId(null); setForm(EMPTY); setFundoFalhou(false); setOpen(true); };
  const abrirEdicao = (p) => {
    setEditId(p.id);
    setFundoFalhou(false);
    setForm({ ...EMPTY, ...p, preco: p.preco ?? "", preco_promocional: p.preco_promocional ?? "", custo: p.custo ?? "", fotos_extras: p.fotos_extras || [], fotos_extras_urls: p.fotos_extras_urls || [] });
    setOpen(true);
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file); fd.append("loja", loja); fd.append("remover_fundo", "false");
    try {
      const r = await axios.post(`${API}/catalogo/upload-foto`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
      setForm((f) => ({ ...f, foto_path: r.data.foto_path, foto_url: r.data.foto_url }));
      setFundoFalhou(false);
      toast.success("Foto enviada");
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao enviar foto"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const refazerFundo = async () => {
    // Mantido só para compatibilidade — não é mais acionado pela UI (remoção IA desativada).
    if (!form.foto_path) return;
    setUploading(true);
    try {
      const r = await axios.post(`${API}/catalogo/refazer-fundo`, { foto_path: form.foto_path, loja }, { timeout: 180000 });
      setForm((f) => ({ ...f, foto_path: r.data.foto_path, foto_url: r.data.foto_url }));
      setFundoFalhou(false);
      toast.success("Fundo removido com sucesso");
    } catch (err) { toast.error(err.response?.data?.detail || "Não foi possível refazer o fundo agora"); }
    finally { setUploading(false); }
  };

  const refazerFundoExtra = async (i) => {
    const path = (form.fotos_extras || [])[i];
    if (!path) return;
    setUploadingExtra(true);
    try {
      const r = await axios.post(`${API}/catalogo/refazer-fundo`, { foto_path: path, loja }, { timeout: 180000 });
      setForm((f) => ({
        ...f,
        fotos_extras: (f.fotos_extras || []).map((p, j) => (j === i ? r.data.foto_path : p)),
        fotos_extras_urls: (f.fotos_extras_urls || []).map((u, j) => (j === i ? r.data.foto_url : u)),
      }));
      toast.success("Fundo removido com sucesso");
    } catch (err) { toast.error(err.response?.data?.detail || "Não foi possível refazer o fundo agora"); }
    finally { setUploadingExtra(false); }
  };

  const onFileExtra = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingExtra(true);
    try {
      for (let i = 0; i < files.length; i++) {
        setExtraProgresso(`${i + 1}/${files.length}`);
        const fd = new FormData(); fd.append("file", files[i]); fd.append("loja", loja); fd.append("remover_fundo", "false");
        const r = await axios.post(`${API}/catalogo/upload-foto`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
        setForm((f) => ({ ...f, fotos_extras: [...(f.fotos_extras || []), r.data.foto_path], fotos_extras_urls: [...(f.fotos_extras_urls || []), r.data.foto_url] }));
      }
      toast.success(files.length === 1 ? "Foto extra enviada" : "Fotos extras enviadas");
    } catch (err) { toast.error("Erro nas fotos extras"); }
    finally { setUploadingExtra(false); setExtraProgresso(""); if (extraRef.current) extraRef.current.value = ""; }
  };
  const removeExtra = (i) => setForm((f) => ({ ...f, fotos_extras: f.fotos_extras.filter((_, idx) => idx !== i), fotos_extras_urls: f.fotos_extras_urls.filter((_, idx) => idx !== i) }));



  const salvar = async () => {
    if (!form.nome.trim()) { toast.error("Nome é obrigatório"); return; }
    if (!form.preco) { toast.error("Preço de venda é obrigatório"); return; }
    const payload = {
      loja, nome: form.nome.trim(), preco: Number(form.preco),
      preco_promocional: form.preco_promocional ? Number(form.preco_promocional) : null,
      categoria: (form.categoria || "").trim(), descricao: form.descricao || "", codigo: form.codigo || "",
      custo: form.custo ? Number(form.custo) : null, vender_por: form.vender_por || "Unidade",
      destacar: !!form.destacar, exibir_catalogo: !!form.exibir_catalogo, foto_path: form.foto_path || null, fotos_extras: form.fotos_extras || [],
    };
    try {
      if (editId) await axios.put(`${API}/catalogo/vitrine/${editId}`, payload);
      else await axios.post(`${API}/catalogo/vitrine`, payload);
      toast.success(editId ? "Produto atualizado" : "Produto adicionado");
      setOpen(false); carregar(loja);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar"); }
  };

  const excluir = async (p) => {
    if (!window.confirm(`Remover "${p.nome}" do catálogo?`)) return;
    try { await axios.delete(`${API}/catalogo/vitrine/${p.id}`); toast.success("Removido"); carregar(loja); }
    catch { toast.error("Erro ao remover"); }
  };

  const reexibir = async (p) => {
    try { await axios.post(`${API}/catalogo/vitrine/${p.id}/reexibir`); toast.success("Produto reexibido no catálogo"); carregar(loja); }
    catch { toast.error("Erro ao reexibir"); }
  };

  const copiarLink = () => {
    const url = `${window.location.origin}/catalogo/${slugify(loja)}`;
    navigator.clipboard.writeText(url).then(() => toast.success("Link copiado")).catch(() => toast.error("Não foi possível copiar o link"));
  };

  const linkDaLoja = (l) => `${window.location.origin}/catalogo/${slugify(l)}`;
  const copiarLinkLoja = (l) => {
    navigator.clipboard.writeText(linkDaLoja(l)).then(() => toast.success(`Link de ${l} copiado`)).catch(() => toast.error("Não foi possível copiar"));
  };

  const descontoPct = (p) => (p.preco_promocional && p.preco_promocional < p.preco ? Math.round((1 - p.preco_promocional / p.preco) * 100) : 0);

  const filtroAtivo = !!busca || !!catFiltro || ordemSort !== "manual" || aba === "ocultos";
  const nAtivos = produtos.filter((p) => p.exibir_catalogo !== false).length;
  const nOcultos = produtos.filter((p) => p.exibir_catalogo === false).length;
  const categoriasLista = [...new Set(produtos.map((p) => p.categoria).filter(Boolean))];
  const produtosExibidos = useMemo(() => {
    let arr = produtos.filter((p) => p.id);
    arr = arr.filter((p) => (aba === "ocultos" ? p.exibir_catalogo === false : p.exibir_catalogo !== false));
    if (catFiltro) arr = arr.filter((p) => p.categoria === catFiltro);
    if (busca) { const q = busca.toLowerCase(); arr = arr.filter((p) => (p.nome || "").toLowerCase().includes(q) || (p.codigo || "").toLowerCase().includes(q) || (p.categoria || "").toLowerCase().includes(q)); }
    if (ordemSort === "recent") arr = [...arr].sort((a, b) => String(b.criado_em).localeCompare(String(a.criado_em)));
    else if (ordemSort === "old") arr = [...arr].sort((a, b) => String(a.criado_em).localeCompare(String(b.criado_em)));
    return arr;
  }, [produtos, busca, catFiltro, ordemSort, aba]);

  // ---- Drag reorder produtos ----
  const onDropProd = async (idx) => {
    const from = dragProd.current; dragProd.current = null;
    if (from === null || from === idx) return;
    const arr = [...produtos]; const [m] = arr.splice(from, 1); arr.splice(idx, 0, m);
    setProdutos(arr);
    try { await axios.post(`${API}/catalogo/vitrine/reordenar`, { loja, ordem: arr.map((p) => p.id) }); toast.success("Ordem dos produtos salva"); }
    catch { toast.error("Erro ao salvar ordem"); }
  };

  // ---- Drag reorder categorias ----
  const onDropCat = (idx) => {
    const from = dragCat.current; dragCat.current = null;
    if (from === null || from === idx) return;
    const arr = [...catOrdem]; const [m] = arr.splice(from, 1); arr.splice(idx, 0, m);
    setCatOrdem(arr);
  };
  const salvarCatOrdem = async () => {
    try { await axios.post(`${API}/catalogo/categorias/ordem`, { loja, categorias: catOrdem }); toast.success("Ordem das categorias salva"); }
    catch { toast.error("Erro ao salvar categorias"); }
  };

  const abrirLeads = async () => {
    setLeadsOpen(true);
    try { const r = await axios.get(`${API}/catalogo/leads`, { params: { loja } }); setLeads(r.data.leads || []); }
    catch { toast.error("Erro ao carregar clientes"); }
  };
  const fmtData = (iso) => iso ? new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "-";

  if (!loja) {
    return (
      <div className="max-w-2xl mx-auto mt-6" data-testid="catalogo-admin-gate">
        <h1 className="text-3xl font-bold text-slate-900 mb-1">Catálogo</h1>
        <p className="text-slate-500 mb-6">Selecione a loja para gerenciar os produtos do catálogo.</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {lojas.map((l) => (
            <button key={l} onClick={() => setLoja(l)} data-testid={`catalogo-gate-${slugify(l)}`}
              className="rounded-xl border border-slate-200 bg-white p-5 text-left hover:border-slate-900 hover:shadow-md transition-all">
              <Store className="w-6 h-6 text-slate-700 mb-2" />
              <p className="font-semibold text-slate-900">{l}</p>
              <p className="text-xs text-slate-400 mt-0.5">Gerenciar catálogo</p>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="catalogo-admin">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Catálogo — {loja}</h1>
          <p className="text-slate-500 text-sm">{produtos.length} produto(s) · arraste os cards para reordenar</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={abrirLeads} data-testid="catalogo-leads-btn"><Users className="w-4 h-4 mr-2" />Clientes</Button>
          <Button variant="outline" onClick={() => setAba("ocultos")} data-testid="catalogo-ver-vendidos"><EyeOff className="w-4 h-4 mr-2" />Vendidos / Ocultos{nOcultos ? ` (${nOcultos})` : ""}</Button>
          <Button variant="outline" onClick={() => setLinksOpen(true)} data-testid="catalogo-criar-link"><Link2 className="w-4 h-4 mr-2" />Criar novo link do catálogo</Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="catalogo-ver-publico"><ExternalLink className="w-4 h-4 mr-2" />Ver / Compartilhar</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-white">
              <DropdownMenuItem onClick={copiarLink} data-testid="catalogo-copiar-link"><Copy className="w-4 h-4 mr-2" />Copiar link</DropdownMenuItem>
              <DropdownMenuItem onClick={() => window.open(`/catalogo/${slugify(loja)}`, "_blank")} data-testid="catalogo-abrir-link"><ExternalLink className="w-4 h-4 mr-2" />Abrir catálogo</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={abrirNovo} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="catalogo-novo-produto"><Plus className="w-4 h-4 mr-2" />Novo produto</Button>
          <Button variant="outline" onClick={() => setLoja(null)} data-testid="catalogo-trocar-loja"><Store className="w-4 h-4 mr-2" />Trocar loja</Button>
        </div>
      </div>

      {/* Ordem das categorias */}
      {catOrdem.length > 1 && (
        <Card className="bg-white border-slate-200" data-testid="cat-ordem">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Tag className="w-4 h-4 text-slate-500" />Ordem das categorias na vitrine (arraste)</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2 mb-3">
              {catOrdem.map((c, idx) => (
                <div key={c} draggable onDragStart={() => { dragCat.current = idx; }} onDragOver={(e) => e.preventDefault()} onDrop={() => onDropCat(idx)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-100 border border-slate-200 text-sm font-medium text-slate-700 cursor-move" data-testid={`cat-chip-${slugify(c)}`}>
                  <GripVertical className="w-3.5 h-3.5 text-slate-400" />{c}
                </div>
              ))}
            </div>
            <Button size="sm" onClick={salvarCatOrdem} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="cat-ordem-salvar">Salvar ordem das categorias</Button>
          </CardContent>
        </Card>
      )}

      {produtos.length === 0 ? (
        <Card className="bg-white border-slate-200 border-dashed"><CardContent className="py-16 text-center text-slate-400">Nenhum produto ainda. Clique em "Novo produto".</CardContent></Card>
      ) : (
        <>
        <div className="flex items-center gap-2" data-testid="catalogo-abas">
          <button onClick={() => setAba("ativos")} data-testid="catalogo-aba-ativos" className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${aba === "ativos" ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-400"}`}>Ativos ({nAtivos})</button>
          <button onClick={() => setAba("ocultos")} data-testid="catalogo-aba-ocultos" className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${aba === "ocultos" ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-400"}`}>Ocultos / Vendidos ({nOcultos})</button>
        </div>
        <div className="flex flex-wrap items-center gap-2 bg-white border border-slate-200 rounded-xl p-3" data-testid="catalogo-filtros">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-2.5" />
            <Input className="pl-8" placeholder="Buscar por referência, nome ou categoria" value={busca} onChange={(e) => setBusca(e.target.value)} data-testid="catalogo-admin-busca" />
          </div>
          <Select value={catFiltro || "all"} onValueChange={(v) => setCatFiltro(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[180px]" data-testid="catalogo-admin-cat"><SelectValue placeholder="Categoria" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Todas as categorias</SelectItem>{categoriasLista.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={ordemSort} onValueChange={setOrdemSort}>
            <SelectTrigger className="w-[170px]" data-testid="catalogo-admin-ordem"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="manual">Ordem manual</SelectItem><SelectItem value="recent">Mais recentes</SelectItem><SelectItem value="old">Mais antigos</SelectItem></SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {produtosExibidos.map((p, idx) => {
            const d = descontoPct(p);
            return (
              <Card key={p.id} draggable={!filtroAtivo} onDragStart={() => { dragProd.current = idx; }} onDragOver={(e) => e.preventDefault()} onDrop={() => { if (!filtroAtivo) onDropProd(idx); }}
                className={`bg-white border-slate-200 overflow-hidden ${filtroAtivo ? "" : "cursor-move"}`} data-testid={`catalogo-item-${p.id}`}>
                <div className="relative aspect-square bg-white flex items-center justify-center overflow-hidden">
                  {p.foto_url ? <img src={`${BACKEND}${p.foto_url}`} alt={p.nome} className="w-full h-full object-contain" /> : <ImageIcon className="w-10 h-10 text-slate-300" />}
                  {d > 0 && <span className="absolute top-2 left-2 text-xs font-bold bg-red-500 text-white px-2 py-0.5 rounded-full">-{d}%</span>}
                  {!p.exibir_catalogo && <span className="absolute top-2 right-2 text-[10px] font-semibold bg-slate-800 text-white px-2 py-0.5 rounded-full">Oculto</span>}
                  {p.destacar && <span className="absolute bottom-2 left-2 text-[10px] font-semibold bg-amber-400 text-slate-900 px-2 py-0.5 rounded-full">Destaque</span>}
                  <GripVertical className="absolute top-2 right-2 w-4 h-4 text-slate-300 opacity-0 group-hover:opacity-100" />
                </div>
                <CardContent className="p-3">
                  {p.categoria ? <p className="text-[10px] uppercase tracking-wide text-slate-400 truncate">{p.categoria}</p> : null}
                  <p className="text-sm font-semibold text-slate-800 truncate">{p.nome}</p>
                  <div className="flex items-baseline gap-1.5 mt-1">
                    {d > 0 && <span className="text-xs text-slate-400 line-through">{fmtBRL(p.preco)}</span>}
                    <span className="text-base font-extrabold text-emerald-600">{fmtBRL(d > 0 ? p.preco_promocional : p.preco)}</span>
                  </div>
                  <div className="flex items-center gap-2.5 text-[10px] text-slate-400 mt-1.5" data-testid={`admin-metrics-${p.id}`}>
                    <span title="Visualizações" className="flex items-center gap-0.5"><Eye className="w-3 h-3" />{p.visualizacoes || 0}</span>
                    <span title="Provas por IA" className="flex items-center gap-0.5"><Sparkles className="w-3 h-3" />{p.provas || 0}</span>
                    <span title="Cliques WhatsApp" className="flex items-center gap-0.5"><MessageCircle className="w-3 h-3" />{p.cliques_whatsapp || 0}</span>
                  </div>
                  <div className="flex gap-1 mt-2">
                    {p.exibir_catalogo === false && <Button size="sm" variant="outline" className="flex-1 h-8 text-emerald-600 border-emerald-200 hover:bg-emerald-50" onClick={() => reexibir(p)} data-testid={`catalogo-reexibir-${p.id}`} title="Reexibir no catálogo"><RotateCcw className="w-3.5 h-3.5" /></Button>}
                    <Button size="sm" variant="outline" className="flex-1 h-8" onClick={() => abrirEdicao(p)} data-testid={`catalogo-editar-${p.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="outline" className="h-8 text-red-600 border-red-200 hover:bg-red-50" onClick={() => excluir(p)} data-testid={`catalogo-excluir-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
        </>
      )}

      {/* Modal cadastro/edição */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg bg-white max-h-[90vh] overflow-y-auto" data-testid="admin-product-form">
          <DialogHeader><DialogTitle>{editId ? "Editar produto" : "Novo produto"}</DialogTitle><DialogDescription>Loja: {loja}</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-24 h-24 rounded-lg border border-slate-200 bg-white flex items-center justify-center overflow-hidden shrink-0">
                {uploading ? <Loader2 className="w-6 h-6 text-slate-400 animate-spin" /> : form.foto_url ? <img src={`${BACKEND}${form.foto_url}`} alt="prévia" className="w-full h-full object-contain" /> : <ImageIcon className="w-8 h-8 text-slate-300" />}
              </div>
              <div className="flex-1">
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="admin-foto-input" />
                <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="admin-button-upload-foto">{uploading ? "Enviando..." : <><ImageIcon className="w-4 h-4 mr-2" />Enviar foto principal</>}</Button>
                <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-2.5" data-testid="admin-canva-dica">
                  <p className="text-xs text-slate-600">
                    <strong>Dica:</strong> para catálogo profissional, deixe o fundo branco antes de enviar. Use o Canva Pro (grátis incluso no seu plano) — tem remoção de fundo com 1 clique.
                  </p>
                  <a href="https://www.canva.com/features/background-remover/" target="_blank" rel="noreferrer"
                     className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:text-emerald-800 hover:underline"
                     data-testid="admin-canva-link">
                    <ExternalLink className="w-3 h-3" />Abrir removedor de fundo no Canva Pro
                  </a>
                </div>
              </div>
            </div>

            {/* Fotos extras */}
            <div>
              <Label className="text-xs text-slate-500">Fotos extras (aparecem na página do produto)</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {(form.fotos_extras_urls || []).map((u, i) => (
                  <div key={u || i} className="relative w-16 h-16 rounded-lg border border-slate-200 overflow-hidden group" data-testid={`admin-extra-${i}`}>
                    <img src={`${BACKEND}${u}`} alt="" className="w-full h-full object-contain" />
                    <button onClick={() => removeExtra(i)} className="absolute top-0.5 right-0.5 bg-red-500 text-white rounded-full p-0.5" data-testid={`admin-extra-remove-${i}`}><X className="w-3 h-3" /></button>
                  </div>
                ))}
                <input ref={extraRef} type="file" accept="image/*" multiple className="hidden" onChange={onFileExtra} data-testid="admin-foto-extra-input" />
                <button onClick={() => extraRef.current?.click()} disabled={uploadingExtra} className="w-16 h-16 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center text-slate-400 hover:border-slate-900" data-testid="admin-foto-extra-btn">{uploadingExtra ? <span className="flex flex-col items-center"><Loader2 className="w-4 h-4 animate-spin" /><span className="text-[10px] mt-0.5">{extraProgresso}</span></span> : <Plus className="w-5 h-5" />}</button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Nome do produto *</Label><Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="admin-nome" /></div>
              <div><Label>Preço de venda *</Label><Input type="number" step="0.01" value={form.preco} onChange={(e) => setForm({ ...form, preco: e.target.value })} data-testid="admin-preco" /></div>
              <div><Label>Preço promocional</Label><Input type="number" step="0.01" value={form.preco_promocional} onChange={(e) => setForm({ ...form, preco_promocional: e.target.value })} data-testid="admin-preco-promo" /></div>
            </div>

            <div>
              <Label className="flex items-center gap-1"><Tag className="w-3.5 h-3.5" /> Categoria</Label>
              <Input value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })} placeholder="Ex: Quadrado Masculino, Solar Feminino..." data-testid="admin-categoria" />
              {catOrdem.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid="admin-categoria-sugestoes">
                  <span className="text-[11px] text-slate-500 mr-1 self-center">Toque para usar:</span>
                  {catOrdem.map((c) => (
                    <button
                      type="button"
                      key={c}
                      onClick={() => setForm({ ...form, categoria: c })}
                      className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${
                        form.categoria === c
                          ? "bg-slate-900 text-white border-slate-900"
                          : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
                      }`}
                      data-testid={`admin-categoria-chip-${c.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-slate-400 mt-1">Dica: inclua "Masculino" ou "Feminino" no nome da categoria para a ordenação por gênero funcionar.</p>
            </div>

            <div>
              <Label>Descrição</Label>
              <Textarea value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} rows={3} data-testid="admin-descricao" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div><Label>Código</Label><Input value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value })} data-testid="admin-codigo" /></div>
              <div><Label>Custo</Label><Input type="number" step="0.01" value={form.custo} onChange={(e) => setForm({ ...form, custo: e.target.value })} data-testid="admin-custo" /></div>
              <div className="col-span-2"><Label>Vender por</Label>
                <Select value={form.vender_por} onValueChange={(v) => setForm({ ...form, vender_por: v })}>
                  <SelectTrigger data-testid="admin-vender-por"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Unidade">Unidade</SelectItem><SelectItem value="Par">Par</SelectItem><SelectItem value="Kit">Kit</SelectItem></SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center justify-between py-1 border-t border-slate-100 pt-3">
              <Label htmlFor="destacar" className="cursor-pointer">Destacar este produto</Label>
              <Switch id="destacar" checked={form.destacar} onCheckedChange={(v) => setForm({ ...form, destacar: v })} data-testid="admin-toggle-highlight" />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="exibir" className="cursor-pointer">Exibir produto no catálogo</Label>
              <Switch id="exibir" checked={form.exibir_catalogo} onCheckedChange={(v) => setForm({ ...form, exibir_catalogo: v })} data-testid="admin-toggle-catalog-visibility" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={salvar} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="admin-salvar-produto">{editId ? "Salvar" : "Adicionar produto"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal relatório de clientes (leads) */}
      <Dialog open={leadsOpen} onOpenChange={setLeadsOpen}>
        <DialogContent className="sm:max-w-lg bg-white max-h-[85vh] overflow-y-auto" data-testid="leads-modal">
          <DialogHeader><DialogTitle>Clientes que acessaram — {loja}</DialogTitle><DialogDescription>{leads.length} acesso(s) registrado(s)</DialogDescription></DialogHeader>
          {leads.length === 0 ? <p className="text-center text-slate-400 py-10">Nenhum cliente ainda.</p> : (
            <div className="divide-y divide-slate-100">
              {leads.map((l) => (
                <div key={l.id} className="py-3" data-testid={`lead-row-${l.id}`}>
                  <div className="flex items-center justify-between">
                    <div><p className="font-semibold text-slate-800 text-sm">{l.nome}</p><span className="text-xs text-slate-500">{l.telefone}</span></div>
                    <span className="text-xs text-slate-400">{fmtData(l.criado_em)}</span>
                  </div>
                  {(l.provas || []).length > 0 && (
                    <div className="mt-2 space-y-1.5">
                      <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide">Provou no provador virtual</p>
                      {l.provas.map((pr, i) => {
                        const cod = pr.codigo || pr.nome;
                        const msg = `Olá, tudo bem? Vi que experimentou o modelo "${cod}" no nosso provador virtual, posso te ajudar a fechar essa compra?`;
                        return (
                          <div key={`${l.id}-${pr.produto_id || pr.nome}-${i}`} className="flex items-center justify-between gap-2 bg-slate-50 rounded-lg px-2.5 py-1.5" data-testid={`lead-prova-${l.id}-${i}`}>
                            <span className="text-sm text-slate-700 truncate flex items-center"><Sparkles className="w-3.5 h-3.5 mr-1 text-amber-500 shrink-0" />{pr.nome}{pr.codigo ? ` (${pr.codigo})` : ""}</span>
                            <a href={`https://wa.me/${(l.telefone || "").replace(/\D/g, "")}?text=${encodeURIComponent(msg)}`} target="_blank" rel="noreferrer"
                              className="shrink-0 text-xs font-bold text-white px-2.5 py-1 rounded-lg flex items-center gap-1" style={{ backgroundColor: "#25D366" }} data-testid={`lead-whats-${l.id}-${i}`}>
                              <MessageCircle className="w-3.5 h-3.5" /> Chamar
                            </a>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Criar novo link do catálogo — escolha da loja */}
      <Dialog open={linksOpen} onOpenChange={setLinksOpen}>
        <DialogContent className="sm:max-w-md bg-white" data-testid="catalogo-links-modal">
          <DialogHeader>
            <DialogTitle>Link do catálogo (todas as lojas)</DialogTitle>
            <DialogDescription>Compartilhe UM único link. O cliente escolhe a loja que quer ver.</DialogDescription>
          </DialogHeader>
          <div className="bg-slate-900 text-white rounded-xl p-4 mb-3" data-testid="catalogo-lp-bloco">
            <p className="text-xs text-slate-300 mb-1">Link da página de escolha de lojas</p>
            <p className="text-sm font-mono break-all" data-testid="catalogo-lp-url">{`${window.location.origin}/lojas`}</p>
            <div className="flex gap-2 mt-3">
              <Button size="sm" className="bg-white text-slate-900 hover:bg-slate-100 flex-1" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/lojas`).then(() => toast.success("Link da página copiado")).catch(() => toast.error("Não foi possível copiar")); }} data-testid="catalogo-lp-copiar"><Copy className="w-3.5 h-3.5 mr-1.5" />Copiar link</Button>
              <Button size="sm" variant="outline" className="border-white/30 text-white hover:bg-white/10" onClick={() => window.open("/lojas", "_blank")} data-testid="catalogo-lp-abrir"><ExternalLink className="w-3.5 h-3.5 mr-1.5" />Abrir</Button>
            </div>
          </div>
          <p className="text-xs font-semibold text-slate-500 mb-2">Links diretos por loja (opcional)</p>
          <div className="space-y-2 max-h-[45vh] overflow-y-auto">
            {lojas.map((l) => (
              <div key={l} className="flex items-center justify-between gap-2 border border-slate-200 rounded-lg px-3 py-2" data-testid={`catalogo-link-row-${slugify(l)}`}>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-800">{l}</p>
                  <p className="text-xs text-slate-400 truncate">/catalogo/{slugify(l)}</p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="sm" variant="outline" className="h-8" onClick={() => copiarLinkLoja(l)} data-testid={`catalogo-link-copiar-${slugify(l)}`}><Copy className="w-3.5 h-3.5 mr-1" />Copiar</Button>
                  <Button size="sm" variant="outline" className="h-8" onClick={() => window.open(linkDaLoja(l), "_blank")} data-testid={`catalogo-link-abrir-${slugify(l)}`}><ExternalLink className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
            ))}
            {lojas.length === 0 && <p className="text-center text-slate-400 py-6 text-sm">Nenhuma loja cadastrada</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
